import { ref, onScopeDispose, computed, readonly } from "vue";
import { newWebSocketRpcSession } from "capnweb";
import { useRuntimeConfig } from "#app";
import { useAuthAdapter } from "./useAuthAdapter.js";
import { useObservability } from "./useObservability.js";
class AuthenticationUnavailableError extends Error {
  constructor() {
    super("Authentication unavailable");
    this.name = "AuthenticationUnavailableError";
  }
}
class TokenUnavailableError extends Error {
  constructor() {
    super("No JWT token available for RPC authentication");
    this.name = "TokenUnavailableError";
  }
}
const createSharedState = () => ref({
  isConnected: false,
  isConnecting: false,
  error: null,
  retryCount: 0
});
let sharedState;
let rpcSession = null;
let websocket = null;
let reconnectTimeoutId = null;
export const useFeatureFlagsConnection = () => {
  if (!sharedState) {
    sharedState = createSharedState();
  }
  const state = sharedState;
  const config = useRuntimeConfig();
  const { workersApiUrl, retryAttempts, reconnectDelay } = config.public.rialticShared;
  const obs = useObservability();
  const getAuthToken = async () => {
    try {
      const authAdapter = useAuthAdapter();
      const isAuthenticated = await authAdapter.isAuthenticated();
      if (!isAuthenticated) {
        throw new AuthenticationUnavailableError();
      }
      const token = await authAdapter.getAccessToken();
      if (!token) {
        console.warn("No JWT token available for RPC authentication");
        obs.event("feature-flags-auth-token-unavailable", {
          url: workersApiUrl
        });
        throw new TokenUnavailableError();
      }
      return token;
    } catch (error) {
      if (error instanceof AuthenticationUnavailableError || error instanceof TokenUnavailableError) {
        throw error;
      }
      obs.error(error, {
        function: "useFeatureFlagsConnection:getAuthToken"
      });
      throw error;
    }
  };
  const createWebSocket = async () => {
    try {
      const token = await getAuthToken();
      const wsUrl = `${workersApiUrl}/rpc/feature-flags`.replace("http", "ws");
      const wsUrlWithAuth = `${wsUrl}?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrlWithAuth);
      return new Promise((resolve, reject) => {
        ws.onopen = () => {
          obs.event("feature-flags-websocket-connected", {
            url: wsUrl
          });
          resolve(ws);
        };
        ws.onerror = () => {
          obs.error(new Error("WebSocket connection failed"), {
            function: "useFeatureFlagsConnection:createWebSocket",
            url: wsUrl
          });
          reject(new Error("WebSocket connection failed"));
        };
        ws.onclose = (event) => {
          obs.event("feature-flags-websocket-closed", {
            code: event.code,
            reason: event.reason
          });
          handleDisconnection();
        };
      });
    } catch (error) {
      if (error instanceof AuthenticationUnavailableError || error instanceof TokenUnavailableError) {
        throw error;
      }
      obs.error(error, {
        function: "useFeatureFlagsConnection:createWebSocket"
      });
      throw error;
    }
  };
  const handleDisconnection = () => {
    state.value.isConnected = false;
    rpcSession = null;
    websocket = null;
    if (state.value.retryCount < retryAttempts) {
      state.value.retryCount++;
      obs.event("feature-flags-reconnection-attempt", {
        attempt: state.value.retryCount,
        maxAttempts: retryAttempts
      });
      reconnectTimeoutId = setTimeout(() => {
        connect();
      }, reconnectDelay);
    } else {
      state.value.error = "Max reconnection attempts exceeded";
      obs.error(new Error("Max RPC reconnection attempts exceeded"), {
        function: "useFeatureFlagsConnection:handleDisconnection",
        retryCount: state.value.retryCount,
        maxRetries: retryAttempts
      });
    }
  };
  const connect = async () => {
    if (state.value.isConnecting || state.value.isConnected) {
      return rpcSession;
    }
    state.value.isConnecting = true;
    state.value.error = null;
    try {
      websocket = await createWebSocket();
      rpcSession = newWebSocketRpcSession(websocket);
      state.value.isConnected = true;
      state.value.isConnecting = false;
      state.value.retryCount = 0;
      obs.event("feature-flags-rpc-session-established", {
        url: workersApiUrl
      });
      return rpcSession;
    } catch (error) {
      if (error instanceof AuthenticationUnavailableError) {
        state.value.isConnecting = false;
        state.value.error = null;
        return null;
      }
      if (error instanceof TokenUnavailableError) {
        state.value.isConnecting = false;
        state.value.error = null;
        handleDisconnection();
        return null;
      }
      state.value.isConnecting = false;
      state.value.error = error instanceof Error ? error.message : "Connection failed";
      obs.error(error, {
        function: "useFeatureFlagsConnection:connect"
      });
      handleDisconnection();
      return null;
    }
  };
  const disconnect = () => {
    if (reconnectTimeoutId) {
      clearTimeout(reconnectTimeoutId);
      reconnectTimeoutId = null;
    }
    if (websocket) {
      websocket.close();
      websocket = null;
    }
    rpcSession = null;
    state.value.isConnected = false;
    state.value.isConnecting = false;
    state.value.retryCount = 0;
    obs.event("feature-flags-rpc-disconnected", {});
  };
  const getSession = async () => {
    if (rpcSession && state.value.isConnected) {
      return rpcSession;
    }
    return await connect();
  };
  onScopeDispose(() => {
    disconnect();
  });
  return {
    state: readonly(state),
    isConnected: computed(() => state.value.isConnected),
    isConnecting: computed(() => state.value.isConnecting),
    error: computed(() => state.value.error),
    connect,
    disconnect,
    getSession
  };
};
